/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useReducer, useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import DocumentTemplateGeneralDataCard from './DocumentTemplateGeneralDataCard';
import { initialState, reducer } from '../../../reducers/generalData/containerReducer';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { documentTemplateValidator } from 'features/documentTemplates/validators';
import { OUTPUT_TYPES_QUERY, CONTENT_TYPES_QUERY, TARGET_AREAS_QUERY, ACTIVE_DOCUMENTS_QUERY } from '../../queries';
import { DOCUMENT_TEMPLATE_UPDATED, DOCUMENT_TEMPLATE_FILE_UPLOADED } from '../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ADD_UPDATE_TEMPLATE } from '../../mutations';
import { useSimpleError } from 'hooks/apolloWrappers';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useFileDownload, useFileUpload, useFileCheck } from 'hooks/fileHandling';
import useLanguageTracker from 'hooks/useLanguageTracker';

const exportTypesInitialValues = [{ id: 'Aspose' }, { id: 'CharismaDMS' }, { id: 'DirectFile' }];

const DocumentTemplateGeneralDataContainer = ({ data, filters, onChangeDocumentTemplateTitle, onRedirect, bulkPrintingYN, loading }) => {
  const [activeService] = useContext(ServiceContext);
  const [localState, dispatch] = useReducer(reducer, initialState);
  const showError = useSimpleError();
  const [t] = useTranslation();
  const addToast = useToast();

  const [documentTemplateValidation, validateDocumentTemplate] = useDirtyFieldValidation(documentTemplateValidator);

  const [exportTypes] = useState(exportTypesInitialValues);
  const [computing, setComputing] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);

  // #region Queries

  const { data: outputTypesData } = useQuery(OUTPUT_TYPES_QUERY);

  const { data: contentTypesData } = useQuery(CONTENT_TYPES_QUERY);

  const contentTypesOptions = useMemo(() => (contentTypesData ? contentTypesData.contentTypes.map(x => x.name) : []), [contentTypesData]);

  const { data: targetAreasData, refetch: refetchTargetAreas } = useQuery(TARGET_AREAS_QUERY);

  const { data: activeDocumentTypesData } = useQuery(ACTIVE_DOCUMENTS_QUERY);

  // #endregion

  // #region Mutations

  const [addOrUpdateDocumentTemplate] = useMutation(ADD_UPDATE_TEMPLATE);

  // #endregion

  // #region Subscriptions

  useSubscription(DOCUMENT_TEMPLATE_UPDATED, {
    onData: ({ data: dataReceived, client }) => {
      addToast(t('DocumentTemplate.SavingSucceeded'), 'success');
      if (data === undefined || data === null) onRedirect(dataReceived.data.documentTemplateUpdated.documentTemplateId);
      client.cache.evict({
        id: `DocumentTemplateItem:{"documentTemplateId":${dataReceived.data.documentTemplateUpdated.documentTemplateId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'templates' });
      client.cache.gc();
    }
  });

  useSubscription(DOCUMENT_TEMPLATE_FILE_UPLOADED, {
    onData: ({ data: { data } }) => {
      setComputing(false);
      setIsFileUploaded(true);
      handlePropertyChanged('localDiskFileName', data.documentTemplateFileUploaded.documentTemplateName);
      addToast(t('DocumentTemplate.FileUploaded'), 'success');
    }
  });

  // #endregion

  // #region File

  const [uploadFile] = useFileUpload({
    onError: () => {
      setComputing(false);
    }
  });

  const [downloadFile] = useFileDownload({
    activeService: activeService,
    onError: error => {
      showError(error);
      setComputing(false);
    },
    onCompleted: () => {
      addToast(t('DocumentTemplate.FileDownloaded'), 'success');
      setComputing(false);
    }
  });

  const [checkFile] = useFileCheck({
    activeService: activeService,
    onError: error => {
      setIsFileUploaded(false);
      showError(error);
    },
    onCompleted: isUploaded => {
      setIsFileUploaded(isUploaded);
    }
  });

  const handleUploadFile = file => {
    setComputing(true);
    uploadFile(file, {
      fileName: file.name,
      documentTemplateId: localState.documentTemplate?.documentTemplateId
    });
  };

  const handleDownloadFile = localDiskFileName => {
    if (localDiskFileName) {
      setComputing(true);
      downloadFile(localDiskFileName);
    }
  };

  const handleLocalDiskFileNameChanged = () => {
    checkFile(localState?.documentTemplate?.localDiskFileName);
  };

  const handleFileSelected = event => {
    if (event.target.files.length > 0) {
      let file = event.target.files[0];
      handleUploadFile(file);
    }
  };

  // #endregion

  // #region Handlers

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };

  const handleSave = () => {
    const {
      changed,
      documentTemplateFilters,
      documentTemplateXUserGroups,
      documentTemplateXSignatures,
      documentTemplateXSignaturesChanged,
      signatureBookmarks,
      signatureBookmarksChanged,
      documentTemplateXOptionBookmarks,
      documentTemplateXOptionBookmarksChanged,
      filterCondition,
      ...documentTemplate
    } = localState.documentTemplate;
    const input = { ...documentTemplate, numberConditions: filters.map(x => x.orderNo) };

    if (!validateDocumentTemplate(input)) return;
    dispatch({ type: 'OnHeaderSaved' });
    addOrUpdateDocumentTemplate({ variables: { input } });
  };

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchTargetAreas();
  }, [refetchTargetAreas]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    var docTemp = {
      ...localState.documentTemplate,
      numberConditions: filters.map(x => x.orderNo)
    };
    validateDocumentTemplate(docTemp);
  }, [filters, localState.documentTemplate, validateDocumentTemplate]);

  useEffect(() => {
    if (loading) return;
    if (data) {
      checkFile(data.localDiskFileName);
      dispatch({ type: 'OnInitState', docTemplate: data });
    } else if (bulkPrintingYN !== undefined) dispatch({ type: 'OnDocumentTemplateAdded', bulkPrintingYN });
  }, [loading, data, bulkPrintingYN, checkFile]);

  useEffect(() => {
    onChangeDocumentTemplateTitle(localState.documentTemplate.templateName);
  }, [localState.documentTemplate.templateName, onChangeDocumentTemplateTitle]);

  // #endregion

  const visibleUniqueByDocument = localState && localState.documentTemplate.targetAreaId === -1;

  return (
    <DocumentTemplateGeneralDataCard
      documentTemplate={localState.documentTemplate}
      outputTypes={outputTypesData?.outputTypes || []}
      contentTypes={contentTypesOptions}
      targetAreas={targetAreasData?.targetAreas || []}
      documentTypes={activeDocumentTypesData?.activeDocumentTypes || []}
      exportTypes={exportTypes}
      onPropertyChanged={handlePropertyChanged}
      onSave={handleSave}
      onDownloadFile={handleDownloadFile}
      onFileSelected={handleFileSelected}
      onLocalDiskFileNameChanged={handleLocalDiskFileNameChanged}
      visibleUniqueByDocument={visibleUniqueByDocument}
      computing={computing}
      isFileUploaded={isFileUploaded}
      validation={documentTemplateValidation}
    />
  );
};

DocumentTemplateGeneralDataContainer.propTypes = {
  data: PropTypes.object,
  filters: PropTypes.array.isRequired,
  onChangeDocumentTemplateTitle: PropTypes.func,
  onRedirect: PropTypes.func,
  bulkPrintingYN: PropTypes.bool,
  loading: PropTypes.bool
};

export default DocumentTemplateGeneralDataContainer;
